// src/screens/ResetPassword.js
import React, { useState, useEffect, useRef } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import fondo from '../assets/imagen11.jpeg';
import { isEmail, isRequired } from '../utils/validators';

const APP_PRIMARY = '#b03060';

export default function ResetPassword() {
  const [email, setEmail] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const navigate = useNavigate();

  const timerRef = useRef(null);
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMensaje('');

    const emailLimpio = String(email || '').trim().toLowerCase();

    // Validación defensiva previa
    const errorEmail = isRequired(emailLimpio) || isEmail(emailLimpio);
    if (errorEmail) {
      setError(errorEmail);
      return;
    }

    setCargando(true);

    try {
      await sendPasswordResetEmail(auth, emailLimpio);

      if (montadoRef.current) {
        // Respuesta neutral que protege la privacidad
        setMensaje(
          'Si este correo se encuentra registrado, recibirás un enlace de restablecimiento en tu bandeja de entrada en los próximos minutos.'
        );
        setEmail('');

        timerRef.current = setTimeout(() => {
          if (montadoRef.current) {
            navigate('/login', { replace: true });
          }
        }, 4500);
      }
    } catch (err) {
      // Manejo defensivo sin imprimir trazas en la consola del navegador
      if (montadoRef.current) {
        // Si el usuario no existe, mantenemos el mensaje de éxito neutral para no delatar la base de datos
        if (err?.code === 'auth/user-not-found') {
          setMensaje(
            'Si este correo se encuentra registrado, recibirás un enlace de restablecimiento en tu bandeja de entrada en los próximos minutos.'
          );
          setEmail('');
          timerRef.current = setTimeout(() => {
            if (montadoRef.current) {
              navigate('/login', { replace: true });
            }
          }, 4500);
        } else if (err?.code === 'auth/invalid-email') {
          setError('El formato del correo electrónico ingresado no es válido.');
        } else if (err?.code === 'auth/too-many-requests') {
          setError('Demasiadas solicitudes en poco tiempo. Intenta nuevamente en unos minutos.');
        } else {
          setError('No fue posible procesar la solicitud en este momento. Intenta de nuevo más tarde.');
        }
      }
    } finally {
      if (montadoRef.current) setCargando(false);
    }
  };

  return (
    <div
      style={{
        backgroundImage: `url(${fondo})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div style={styles.overlay}>
        <div style={styles.card}>
          <button onClick={() => navigate(-1)} style={styles.backButton}>
            <FaArrowLeft size={13} /> Volver atrás
          </button>

          <h2 style={styles.title}>Recuperar Contraseña</h2>
          <p style={styles.subtitle}>
            Ingresa tu correo para recibir las instrucciones seguras de restablecimiento.
          </p>

          {error && <div style={styles.errorBox}>{error}</div>}
          {mensaje && <div style={styles.successBox}>{mensaje}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            <input
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={cargando || Boolean(mensaje)}
              required
              maxLength={80}
              autoComplete="email"
              style={styles.input}
            />

            <button
              type="submit"
              disabled={cargando || Boolean(mensaje)}
              style={{
                ...styles.button,
                opacity: cargando || mensaje ? 0.7 : 1,
                cursor: cargando || mensaje ? 'not-allowed' : 'pointer',
              }}
            >
              {cargando ? 'Procesando solicitud...' : 'Enviar correo seguro'}
            </button>
          </form>

          <div style={styles.linkContainer}>
            <span style={{ color: '#475569', fontSize: 13 }}>¿Ya tienes cuenta? </span>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={styles.linkButton}
            >
              Inicia sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== ESTILOS ==========
const styles = {
  overlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    width: '100%',
    maxWidth: 420,
    borderRadius: 22,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)',
    padding: '32px 28px',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    border: '1px solid rgba(255, 255, 255, 0.7)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: APP_PRIMARY,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    marginBottom: 16,
    padding: 0,
  },
  title: {
    color: '#1e293b',
    textAlign: 'center',
    fontSize: 24,
    fontWeight: '700',
    letterSpacing: '-0.3px',
    margin: '0 0 6px 0',
  },
  subtitle: {
    color: '#64748b',
    fontSize: 13,
    textAlign: 'center',
    margin: '0 0 20px 0',
    lineHeight: 1.5,
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    padding: '10px 14px',
    borderRadius: 10,
    marginBottom: 16,
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 1.4,
  },
  successBox: {
    backgroundColor: '#f0fdf4',
    color: '#15803d',
    border: '1px solid #bbf7d0',
    padding: '12px 14px',
    borderRadius: 10,
    marginBottom: 16,
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 1.4,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    width: '100%',
  },
  input: {
    padding: '12px 14px',
    width: '100%',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    color: '#1e293b',
    backgroundColor: '#ffffff',
    transition: 'border-color 0.2s ease',
    fontFamily: 'inherit',
  },
  button: {
    marginTop: 6,
    padding: '13px',
    backgroundColor: APP_PRIMARY,
    color: '#ffffff',
    border: 'none',
    borderRadius: 50,
    cursor: 'pointer',
    width: '100%',
    fontSize: 14,
    fontWeight: '700',
    boxShadow: '0 4px 14px rgba(176, 48, 96, 0.3)',
    transition: 'all 0.2s ease',
  },
  linkContainer: {
    marginTop: 16,
    textAlign: 'center',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: APP_PRIMARY,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '700',
    padding: 0,
    textDecoration: 'none',
  },
};